import React, { useState, useEffect } from "react";
import { ChevronDown, Phone, MessageCircle, MapPin, Quote, Clock, Star } from "lucide-react";
import { CTAButton, Reveal, SectionTitle, scrollToOrder } from "./common";
import { useContent } from "../../context/ContentContext";

export const Testimonials = () => {
  const { testimonials } = useContent();
  return (
    <section className="py-16 bg-[#FBF5E9]">
      <div className="max-w-5xl mx-auto px-4">
        <SectionTitle kicker="Proven On Lakhs Of Customers" title="Words From Our Blessed Family" />
        <div className="grid md:grid-cols-3 gap-5">
          {(testimonials || []).slice(0, 6).map((t, i) => (
            <Reveal key={t.id || t.name} delay={i * 90}>
              <div className="h-full bg-white rounded-xl border border-[#e7d9b8] p-6 shadow-sm">
                <div className="flex items-center gap-1 mb-1">
                  {[...Array(Math.round(t.rating || 5))].map((_, k) => (<Star key={k} className="w-4 h-4 fill-[#E9C767] text-[#E9C767]" />))}
                </div>
                <Quote className="w-8 h-8 text-[#C9992B]/50" />
                <p className="text-[#4a3a2e] text-sm leading-relaxed mt-2">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-4 pt-4 border-t border-[#e7d9b8] flex items-center gap-3">
                  {t.image ? (<img src={t.image} alt={t.name} className="w-9 h-9 rounded-full object-cover" />) : null}
                  <div>
                    <div className="font-display font-bold text-[#5A0B1D]">{t.name}</div>
                    <div className="text-xs text-[#8a6d2f]">{t.location}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CountdownBanner = () => {
  const { countdown } = useContent();
  const startHours = Number(countdown?.hours) > 0 ? Number(countdown.hours) : 6;
  const [t, setT] = useState({ h: startHours, m: 0, s: 0 });
  useEffect(() => {
    const id = setInterval(() => {
      setT((p) => {
        let { h, m, s } = p;
        if (s > 0) s--; else if (m > 0) { m--; s = 59; } else if (h > 0) { h--; m = 59; s = 59; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  if (countdown && countdown.enabled === false) return null;
  return (
    <section className="py-12 bg-[#5A0B1D]">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <div className="inline-flex items-center gap-2 text-[#E9C767] font-marcellus tracking-widest text-sm uppercase mb-3">
          <Clock className="w-4 h-4" /> {countdown?.kicker || "Why Time Is Of The Essence"}
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#F4E3B8]">{countdown?.title || "Limited Energised Pieces Remaining"}</h2>
        <p className="text-[#e0c9ac] mt-2 text-sm">{countdown?.subtitle}</p>
        <div className="flex justify-center gap-3 mt-6">
          {[["Hours", t.h], ["Minutes", t.m], ["Seconds", t.s]].map(([label, val]) => (
            <div key={label} className="bg-[#3a0713] border border-[#C9992B]/40 rounded-xl w-20 py-3">
              <div className="font-display text-3xl font-bold text-[#E9C767]">{pad(val)}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#c9b59a]">{label}</div>
            </div>
          ))}
        </div>
        <div className="mt-8"><CTAButton /></div>
      </div>
    </section>
  );
};

export const FAQ = () => {
  const { faqs } = useContent();
  const [open, setOpen] = useState(0);
  return (
    <section className="py-16 bg-[#FBF5E9]">
      <div className="max-w-3xl mx-auto px-4">
        <SectionTitle kicker="Still Wondering?" title="Frequently Asked Questions" />
        <div className="space-y-3">
          {(faqs || []).map((f, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#e7d9b8] overflow-hidden">
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                <span className="font-display font-semibold text-[#5A0B1D]">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-[#C9992B] shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              <div className={`grid transition-all duration-300 ${open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <div className="overflow-hidden"><p className="px-5 pb-4 text-sm text-[#4a3a2e] leading-relaxed">{f.a}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const TermsConditions = () => {
  const terms = [
    "No refund/exchange/cashback/warranty at any circumstances. Final sale, no exception.",
    "No guarantee for any results. No luck/results are promised ever. Sold 'as is' for belief only. No future claims — you waive all rights for law suits, complaints, media or claims against us. Demands lead to prosecution.",
    "Subject to Malkajgiri jurisdiction.",
    "All Ratna's are recrystallized, Lab/factory enhanced & energised in Vedic Pooja, Homam, Yagnam & Mantram. We give free Certificate, Pooja, Homam, Yagnam. We do not accept any other testing reports. All prescription & pooja mantradiksha are free. All holy things are prescribed only for Hindus; Christians, Muslims & other religions are not insisted to use mantras — they are given other ways & suggestions for power enhancement. Our guarantee for our Gold is only 90% exchange, 80% cash at market rate for KDM Gold.",
  ];
  return (
    <section data-testid="terms-conditions" className="bg-[#FBF5E9] pt-4 pb-12">
      <div className="max-w-5xl mx-auto px-4">
        <div className="rounded-2xl border border-[#C9992B]/40 bg-[#fffaf0] p-6 sm:p-8 shadow-sm">
          <h3 className="font-display text-lg sm:text-xl font-bold text-[#5A0B1D] mb-4 flex items-center gap-2">
            <span className="inline-block w-1.5 h-5 rounded-full bg-[#C9992B]" />
            Terms &amp; Conditions
          </h3>
          <ol className="space-y-3 list-decimal pl-5 marker:text-[#B8860B] marker:font-bold">
            {terms.map((t, i) => (
              <li key={i} data-testid={`terms-item-${i + 1}`} className="text-xs sm:text-sm text-[#4a3a2e] leading-relaxed">
                {t}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { footer } = useContent();
  const phone = footer.phone || footer.whatsapp || "9399993389";
  const whatsapp = footer.whatsapp || "919399993389";
  return (
    <footer className="bg-[#3a0713] text-[#e0c9ac] pt-14 pb-8">
      <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-8">
        <div>
          <h3 className="font-display text-xl font-bold text-[#F4E3B8]">{footer.storeName || footer.name || "ADRUSTAR RATNALAYAM"}</h3>
          <p className="flex items-start gap-2 text-sm mt-3 max-w-sm">
            <MapPin className="w-4 h-4 text-[#C9992B] shrink-0 mt-0.5" /> {footer.address}
          </p>
          <div className="flex gap-3 mt-5">
            <a href={`tel:+${whatsapp}`}>
              <span className="inline-flex items-center gap-2 border border-[#C9992B]/50 rounded-full px-4 py-2 text-sm hover:bg-[#C9992B]/10"><Phone className="w-4 h-4" /> {phone}</span>
            </a>
            <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
              <span className="inline-flex items-center gap-2 border border-[#25D366]/60 text-[#7be0a0] rounded-full px-4 py-2 text-sm hover:bg-[#25D366]/10"><MessageCircle className="w-4 h-4" /> WhatsApp</span>
            </a>
          </div>
        </div>
        <div className="md:text-right">
          <h4 className="font-display font-bold text-[#F4E3B8] mb-3">{footer.ctaTitle || "Change Your Destiny Today"}</h4>
          <p className="text-sm mb-4 md:ml-auto md:max-w-xs">{footer.ctaText || "One Adrusta Ratnam is all you need to begin your journey towards abundance."}</p>
          <button onClick={scrollToOrder} className="inline-block bg-[#C9992B] hover:bg-[#b8860b] text-[#3a1002] font-semibold px-6 py-3 rounded-full transition-colors">Order Now</button>
        </div>
      </div>
      <div className="section-divider max-w-5xl mx-auto my-8 opacity-40" />
      <p className="text-center text-xs text-[#a98f70]">© {new Date().getFullYear()} {footer.storeName || "ADRUSTAR RATNALAYAM"}. All rights reserved.</p>
    </footer>
  );
};

export const StickyBar = () => (
  <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-[#3a0713]/95 backdrop-blur border-t border-[#C9992B]/40 px-4 py-2.5">
    <button onClick={scrollToOrder} className="cta-pulse w-full rounded-lg py-3 font-display font-extrabold uppercase text-[#3a1002] text-sm" style={{ background: "linear-gradient(180deg,#F2D06B,#C9992B)", border: "2px solid #8a6410" }}>
      Get My Adrusta Ratnam →
    </button>
  </div>
);
